import { useCallback, useEffect, useRef, useState } from "react";
import { FFmpeg } from "@ffmpeg/ffmpeg";
import { fetchFile, toBlobURL } from "@ffmpeg/util";

interface UseVideoWatermarkOptions {
  videoFile: File | null;
  watermarkImage: File | null;
  onProgress?: (progress: number) => void;
  onComplete?: (videoUrl: string) => void;
  onError?: (error: Error) => void;
}

interface UseVideoWatermarkReturn {
  isLoading: boolean;
  isProcessing: boolean;
  progress: number;
  watermarkedVideoUrl: string | null;
  watermarkedVideoFile: File | null;
  error: Error | null;
  processVideo: () => Promise<void>;
  reset: () => void;
}

export const useVideoWatermark = ({
  videoFile,
  watermarkImage,
  onProgress,
  onComplete,
  onError,
}: UseVideoWatermarkOptions): UseVideoWatermarkReturn => {
  const [isLoading, setIsLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [watermarkedVideoUrl, setWatermarkedVideoUrl] = useState<string | null>(null);
  const [watermarkedVideoFile, setWatermarkedVideoFile] = useState<File | null>(null);
  const [error, setError] = useState<Error | null>(null);

  const ffmpegRef = useRef<FFmpeg | null>(null);
  const isFFmpegLoadedRef = useRef(false);

  useEffect(() => {
    const loadFFmpeg = async () => {
      if (isFFmpegLoadedRef.current) return;

      try {
        setIsLoading(true);
        const ffmpeg = new FFmpeg();

        ffmpeg.on("log", ({ message }) => {
          console.log("[FFmpeg]", message);
        });

        ffmpeg.on("progress", ({ progress: prog }) => {
          const percentage = Math.round(prog * 100);
          setProgress(percentage);
          onProgress?.(percentage);
        });

        const baseURL = "https://cdn.jsdelivr.net/npm/@ffmpeg/core@0.12.10/dist/esm";

        await ffmpeg.load({
          coreURL: await toBlobURL(`${baseURL}/ffmpeg-core.js`, "text/javascript"),
          wasmURL: await toBlobURL(`${baseURL}/ffmpeg-core.wasm`, "application/wasm"),
        });

        ffmpegRef.current = ffmpeg;
        isFFmpegLoadedRef.current = true;
        console.log("FFmpeg loaded successfully");
      } catch (err) {
        const error = err instanceof Error ? err : new Error("Failed to load FFmpeg");
        console.error("FFmpeg load error:", error);
        setError(error);
        onError?.(error);
      } finally {
        setIsLoading(false);
      }
    };

    loadFFmpeg();

    return () => {
      if (watermarkedVideoUrl) {
        URL.revokeObjectURL(watermarkedVideoUrl);
      }
    };
  }, []);

  const getVideoDimensions = (file: File): Promise<{ width: number; height: number }> => {
    return new Promise((resolve, reject) => {
      const video = document.createElement("video");
      video.preload = "metadata";

      video.onloadedmetadata = () => {
        URL.revokeObjectURL(video.src);
        const width = video.videoWidth || 240; // Default fallback
        const height = video.videoHeight || 426; // Default fallback
        resolve({ width, height });
      };

      video.onerror = () => {
        URL.revokeObjectURL(video.src);
        // Return defaults on error
        resolve({ width: 240, height: 426 });
      };

      video.src = URL.createObjectURL(file);
    });
  };

  const processVideo = useCallback(async () => {
    if (!videoFile || !watermarkImage || !ffmpegRef.current) {
      const error = new Error("Video file, watermark image, or FFmpeg not available");
      setError(error);
      onError?.(error);
      return;
    }

    try {
      setIsProcessing(true);
      setProgress(0);
      setError(null);

      const ffmpeg = ffmpegRef.current;

      // Get video dimensions
      console.log("Getting video dimensions...");
      const { width: videoWidth, height: videoHeight } = await getVideoDimensions(
        videoFile,
      );
      console.log("Video dimensions:", videoWidth, "x", videoHeight);

      // Calculate watermark sizes based on video resolution
      // Logo: 50% of video width, ensuring even number
      const logoWidth = Math.floor((videoWidth * 0.5) / 2) * 2;

      // Padding: 4% of width and 3% of height
      const horizontalPadding = Math.floor(videoWidth * 0.04);
      const verticalPadding = Math.floor(videoHeight * 0.03);

      console.log("Calculated dimensions:", {
        logoWidth,
        horizontalPadding,
        verticalPadding,
      });

      const videoExtension = videoFile.name.split(".").pop()?.toLowerCase() || "mp4";
      const imageExtension = watermarkImage.name.split(".").pop()?.toLowerCase() || "png";

      const inputVideoName = `input.${videoExtension}`;
      const watermarkImageName = `watermark.${imageExtension}`;
      const outputVideoName = `output.mp4`;

      console.log("Writing input files to FFmpeg FS...");

      // Write video and image files
      await ffmpeg.writeFile(inputVideoName, await fetchFile(videoFile));
      await ffmpeg.writeFile(watermarkImageName, await fetchFile(watermarkImage));

      console.log("Files written successfully. Starting FFmpeg processing...");

      const ffmpegArgs = [
        "-i",
        inputVideoName,
        "-i",
        watermarkImageName,
        "-filter_complex",
        `[1:v]format=rgba,scale=${logoWidth}:-2[logo];` +
          "[logo]colorchannelmixer=aa=0.50[logo_transparent];" +
          `[0:v][logo_transparent]overlay=W-w-${horizontalPadding}:H-h-${verticalPadding}`,
        "-c:v",
        "libx264",
        "-preset",
        "ultrafast",
        "-c:a",
        "copy",
        "-y",
        outputVideoName,
      ];

      console.log("FFmpeg command:", ffmpegArgs.join(" "));

      await ffmpeg.exec(ffmpegArgs);

      console.log("FFmpeg processing complete. Reading output file...");

      const data = await ffmpeg.readFile(outputVideoName);
      console.log("Output file size:", data.length, "bytes");

      const blob = new Blob([data], { type: "video/mp4" });
      const url = URL.createObjectURL(blob);

      const watermarkedFile = new File([blob], `watermarked-${videoFile.name}`, {
        type: "video/mp4",
        lastModified: Date.now(),
      });

      // Clean up files
      await ffmpeg.deleteFile(inputVideoName);
      await ffmpeg.deleteFile(watermarkImageName);
      await ffmpeg.deleteFile(outputVideoName);

      setWatermarkedVideoUrl(url);
      setWatermarkedVideoFile(watermarkedFile); // Add this state
      setProgress(100);
      onComplete?.(url);
    } catch (err) {
      const error = err instanceof Error ? err : new Error("Video processing failed");
      console.error("Processing error:", err);
      setError(error);
      onError?.(error);
    } finally {
      setIsProcessing(false);
    }
  }, [videoFile, watermarkImage, onComplete, onError, onProgress]);

  const reset = useCallback(() => {
    if (watermarkedVideoUrl) {
      URL.revokeObjectURL(watermarkedVideoUrl);
    }
    setWatermarkedVideoUrl(null);
    setProgress(0);
    setError(null);
  }, [watermarkedVideoUrl]);

  return {
    isLoading,
    isProcessing,
    progress,
    watermarkedVideoUrl,
    watermarkedVideoFile,
    error,
    processVideo,
    reset,
  };
};
